import { useState, useEffect } from "react";
import "./PostModel.css";
import TickIcon from "../images/tick.svg";
import CloseIcon from "../images/close-icon.svg";
import UploadImageIcon from "../images/image-upload.svg";
import { Upload, Image } from "antd";
import api from "./api";
import { notification } from "antd";
import { useNavigate } from "react-router-dom";
import SubmittedIcon from "../images/post-submitted.svg";
import { trackCuruChatPost, trackProductTag } from './xpUtils';


// Helper function to convert file to base64
const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const PostModel = ({
  profile,
  isOpen,
  onClose,
  preSelectedCategory,
  preSelectedProduct,
}) => {
  const [categorySelected, setCategorySelected] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [postText, setPostText] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categories, setCategories] = useState([]); // New state for categories
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const res = await api.get("/comunity/category"); // Adjust endpoint as needed
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch categories when modal is opened
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
      fetchCategories(); // Fetch categories when modal is open
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);

  // Pre-fill category if preSelectedCategory is passed
  useEffect(() => {
    if (preSelectedCategory) {
      setCategorySelected(preSelectedCategory);
    }
  }, [preSelectedCategory]);

  // Pre-fill product if preSelectedProduct is passed
  useEffect(() => {
    if (preSelectedProduct) {
      setSelectedProduct(preSelectedProduct);
      setProductQuery(preSelectedProduct.name); // Pre-fill product query with the name
      setShowSuggestions(false); // Disable suggestions since the product is selected
    }
  }, [preSelectedProduct]);

  function handleCategoryChange(category) {
    setCategorySelected(category);
    setSubmitError("");
    const dropdownMenu = document.querySelector(".dropdown-menu");
    const dropdownButton = document.getElementById("postDropdown");
    if (dropdownMenu && dropdownButton) {
      dropdownMenu.classList.remove("show");
      dropdownButton.setAttribute("aria-expanded", "false");
      setDropdownOpen(false);
    }
  }

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = async ({ fileList: newFileList }) => {
    const processedFileList = await Promise.all(
      newFileList.map(async (file) => {
        if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj);
        }
        return { ...file, status: "done" };
      })
    );
    setFileList(processedFileList);
  };

  const handlePostSubmit = async () => {
    localStorage.removeItem('earnedXP');
    // Log the data before submitting
    /*
    console.log("Submitting post with data:");
    console.log({
      category: categorySelected,
      message: postText.trim(),
      product: selectedProduct?.id,
      photos: fileList,
    });
    */
    const selectedCategory = categories.find(
      (cat) => cat.name === categorySelected
    );
    const trimmedMessage = postText.trim();
    // Prevent multiple triggers
    if (isSubmitting) return;

    // Lock the form
    setIsSubmitting(true);

    if (!selectedCategory) {
      setSubmitError("Please select a valid category.");
      setIsSubmitting(false); // ✅ Turn off submitting on error
      return;
    }

    if (trimmedMessage.length < 10) {
      setSubmitError("The post message must contain at least 10 characters.");
      setIsSubmitting(false); // ✅ Turn off submitting on error
      return;
    }

    // Prepare form data
    const formData = new FormData();

    // Adding category to form data

    if (selectedCategory) {
      formData.append("category", selectedCategory.id);
    } else {
      setSubmitError("Please select a valid category.");
      return; // Stop form submission
    }

    if (trimmedMessage.length < 10) {
      setSubmitError("The post message must contain at least 10 characters.");
      return; // Stop form submission
    } else {
      formData.append("message", trimmedMessage);
    }

    // Adding tagged product if there is one
    if (selectedProduct !== null) {
      formData.append("product", selectedProduct.id);
    }

    // Convert base64 to Blob for photos (if it's a base64 image string)
    if (fileList.length > 0) {
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("photos", file.originFileObj); // Add the file object directly
        } else if (file.photo) {
          // If it's a base64 string, convert it to a Blob
          const base64String = file.photo;
          const byteCharacters = atob(base64String.split(",")[1]);
          const byteArray = new Uint8Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
          }

          const blob = new Blob([byteArray], { type: "image/svg+xml" }); // Adjust mime type if necessary
          formData.append("photos", blob, "image.svg"); // Adjust file name if necessary
        }
      });
    }

    // Debug: Log FormData contents
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    try {
      const token =
        "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ0b2tlbl90eXBlIjoiYWNjZXNzIiwiZXhwIjoxNzQ2MjUzNTczLCJqdGkiOiI4ZmZhYzAxZWNiOWQ0YjY5YjIxODFhNDhmM2NiNzYzZSIsInVzZXJfaWQiOjI2MzF9.2BLkuG-55oJoLVuFheNU41PU0VX1dqrEzNSqlM0f6Hk";
      const response = await api.post("/comunity/post", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      console.log("Post created successfully:", response.data);

      // Award XP for creating a post
      console.log("Post successful - tracking XP for Curu Chat post");
      trackCuruChatPost();
          
      // If a product was tagged, award additional XP
      if (selectedProduct !== null) {
        console.log("Product tagged - tracking additional XP");
        trackProductTag();
      }  

      setShowSuccessPopup(true);
    } catch (err) {
      console.error("Error creating post:", err);
      if (err.response) {
        console.log("Response error data:", err.response.data);
        console.log("Response status:", err.response.status);
        console.log("Response headers:", err.response.headers);
      } else {
        console.log("Error message:", err.message);
      }
      // Error Notification
      notification.error({
        message: "Error",
        description: "There was an error creating the post.",
        placement: "topRight",
      });
    } finally {
      setIsSubmitting(false); // Unlock the form
    }
  };

  // Used for live search of products
  useEffect(() => {
    const fetchProducts = async (query) => {
      try {
        const res = await api.get("/products", {
          params: { search: query },
        });
        return res.data || [];
      } catch (error) {
        console.error("Error fetching products:", error);
        return [];
      }
    };

    const delayDebounce = setTimeout(() => {
      if (productQuery.length > 1 && showSuggestions) {
        fetchProducts(productQuery).then((results) => {
          setProductResults(results);
        });
      } else {
        setProductResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [productQuery, showSuggestions]);

  if (!isOpen) return null;

  return (
    <>
      <div className="model-backdrop fade show"></div>
      {!showSuccessPopup && (
        <div className="post-container">
          <div className="user-info">
            <img
              className="profile-icon"
              src={profile?.photo || "/default-profile-pic.jpg"}
              alt=""
            />
            <p className="user-name">{profile.username}</p>
          </div>

          <div className="exit-button">
            <img
              className="exit"
              onClick={onClose}
              src={CloseIcon}
              alt="Exit"
            />
          </div>

          <div className="category">
            <div className="choose-category">
              <button
                className={`category-dropdown ${
                  categorySelected !== "" && "category-selected"
                } dropdown-toggle`}
                type="button"
                id="postDropdown"
                data-bs-toggle="dropdown"
                aria-expanded={dropdownOpen ? "true" : "false"}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {categorySelected || "Choose a category"}
              </button>

              <ul className="dropdown-menu" aria-labelledby="postDropdown">
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <li key={category.id}>
                      <button
                        className="dropdown-item"
                        type="button"
                        onClick={() => handleCategoryChange(category.name)}
                      >
                        {category.name}
                        {category.name === categorySelected && (
                          <img
                            className="tick-icon"
                            src={TickIcon}
                            alt="Selected"
                          />
                        )}
                      </button>
                    </li>
                  ))
                ) : (
                  <li>No categories found</li>
                )}
              </ul>
            </div>

            <div className="search-product">
              <svg
                width="15"
                height="20"
                className="search-icon"
                viewBox="0 0 20 20"
              >
                <path
                  d="M14.386 14.386l4.0877 4.0877-4.0877-4.0877c-2.9418 2.9419-7.7115 2.9419-10.6533 0-2.9419-2.9418-2.9419-7.7115 0-10.6533 2.9418-2.9419 7.7115-2.9419 10.6533 0 2.9419 2.9418 2.9419 7.7115 0 10.6533z"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="search"
                placeholder="Search product to mention"
                className="search-input"
                autoComplete="off"
                value={productQuery}
                onChange={(e) => {
                  setProductQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                disabled={selectedProduct !== null}
              />
            </div>

            <div className="text-container">
              {showSuggestions && productQuery.length > 1 && (
                <div className="results-box wide">
                  {productResults.length > 0 ? (
                    productResults.map((product) => (
                      <div
                        key={product.id}
                        className="result-item"
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowSuggestions(false);
                          setProductQuery(product.name);
                        }}
                      >
                        <img
                          src={
                            product.image
                              ? product.image
                              : "https://placehold.co/260x360/FFF/c2c2c2?text=No+Picture"
                          }
                          alt={product.name}
                          className="result-image"
                        />
                        <div className="result-text">
                          <div className="brand-name">
                            {product.brand?.name || "Unknown"}
                          </div>
                          <div className="product-name">{product.name}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div>Product not found.</div>
                  )}
                </div>
              )}

              {selectedProduct !== null && (
                <div className="selected-products">
                  <div
                    key={selectedProduct.id}
                    className="selected-product-tag"
                  >
                    <img
                      src={
                        selectedProduct.image
                          ? selectedProduct.image
                          : "https://placehold.co/260x360/FFF/c2c2c2?text=No+Picture"
                      }
                      alt={selectedProduct.name}
                      className="selected-product-image"
                    />
                    <div className="selected-product-name">
                      {selectedProduct.name}
                    </div>
                    <button
                      className="remove-product-btn"
                      onClick={() => {
                        setSelectedProduct(null);
                        setProductQuery("");
                        setShowSuggestions(false);
                      }}
                      title="Remove"
                    >
                      <img className="remove-icon" src={CloseIcon} alt="x" />
                    </button>
                  </div>
                </div>
              )}
              <div className="textarea-wrapper">
                <textarea
                  className="text-field"
                  rows="6"
                  placeholder="Post message"
                  value={postText}
                  onChange={(e) => {
                    setPostText(e.target.value);
                    // Ensure submitError matches the error message for empty post.
                    if (
                      submitError ===
                        "The post message must contain at least 10 characters." &&
                      postText.trim().length >= 10
                    ) {
                      setSubmitError("");
                    }
                  }}
                />
              </div>

              <div className="upload-photo">
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  maxCount={10}
                  accept="image/*"
                  onPreview={handlePreview}
                  onChange={handleChange}
                >
                  {fileList.length >= 10 ? null : (
                    <button className="upload-button" type="button">
                      <img src={UploadImageIcon} alt="Upload" />
                    </button>
                  )}
                </Upload>
              </div>
            </div>
            <div className="submit-area">
              {submitError && <p className="error-message">{submitError}</p>}

              <button
                className="submit-button"
                type="button"
                onClick={() =>
                  handlePostSubmit(
                    postText,
                    categorySelected,
                    selectedProduct,
                    fileList
                  )
                }
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Image
        preview={{ visible: previewOpen, onVisibleChange: setPreviewOpen }}
        src={previewImage}
      />

      {showSuccessPopup && (
        <>
          <div className="model-backdrop fade show"></div>
          <div className="post-success-popup">
            <img
              src={SubmittedIcon}
              alt="Submitted icon"
              className="post-success-icon"
            />
            <p className="post-success-message">Post Submitted!</p>
            <p className="post-success-description">Thanks for sharing!</p>
            <button
              className="submit-button"
              onClick={() => {
                setShowSuccessPopup(false);
                onClose();
                navigate(0);
              }}
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default PostModel;
